/**
 * The OKF tab's data source (FRONT-1605): a plain, unpaginated read of the
 * knowledge catalogue's ALWAYS-RESIDENT
 * OVERVIEW (`GET /api/files-catalogue` with no query params —
 * `src/files/routes.ts`), the exact `OverviewCatalogueData` shape
 * `src/files/catalogue.ts`'s `readKnowledgeOverview` returns (wire types
 * below mirror `src/files/catalogue-render.ts` and
 * `src/retrieval/catalogue.ts`'s exported shapes 1:1 — `Date` fields cross
 * the wire as ISO strings).
 *
 * This is deliberately NOT the same read the `file_catalogue` agent tool
 * uses (the on-demand COMPLETE listing, narrowed by `type`/`folder`) — the
 * overview's real per-type totals/teasers are a completely different shape
 * the tab renders as cards server-side already (`sections`), so there is
 * nothing left for this hook to narrow or group client-side. No folder
 * param, no varying input: the overview's own query key is a fixed
 * constant.
 *
 * The per-type LISTING that used to live here went with the dialog it
 * backed: every "show me these items" surface now reads the faceted
 * retrieval engine instead (`use-knowledge-search.ts`), which can do what
 * the catalogue listing could not — span every kind at once, and narrow by
 * tag. The catalogue listing endpoint stays, unchanged, for the
 * `file_catalogue` agent tool.
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '~/lib/api-client';

export type KnowledgeSource = 'file' | 'document';

/** Wire shape from `CatalogueRow` (`src/retrieval/catalogue.ts`). */
export interface CatalogueRow {
  source: KnowledgeSource;
  /** `user_files.id` for a file, `memory_documents.filename` for a document
   *  — the stable identifier to key UI/tests on, never `title` (a
   *  model/human-authored string an earlier run can legitimately repeat). */
  ref: string;
  title: string;
  description: string;
  okfType: string | null;
  /** `src/okf/trust.ts`'s `knowledgeTrust`, range `[0.125, 1.0]`. */
  trust: number;
  stale: boolean;
  updatedAt: string;
}

export interface CatalogueFolderSummary {
  name: string;
  count: number;
}

/** One OKF type's REAL footprint in the corpus (never a count of `items`,
 *  which is only a small teaser — `src/files/catalogue-render.ts`'s
 *  `TOP_PER_TYPE_MAX`), plus its top few entries. `type: null` is the
 *  "no type set" bucket. */
export interface TypeSection {
  type: string | null;
  total: number;
  staleCount: number;
  items: CatalogueRow[];
}

export interface OverviewCatalogueData {
  scope: 'overview';
  totals: { files: number; documents: number; folders: number };
  /** Ordered by `total` desc, server-side — the type with the most items leads. */
  sections: TypeSection[];
  folders: CatalogueFolderSummary[];
  foldersTotal: number;
}

export const catalogueKeys = {
  all: ['knowledge-catalogue'] as const,
  /** The overview has no varying input — one fixed key, kept as a `.overview()`
   *  accessor (rather than a bare literal at call sites) so a future
   *  invalidation call has one place to import from. */
  overview: () => [...catalogueKeys.all, 'overview'] as const,
} as const;

function fetchOverview(): Promise<OverviewCatalogueData> {
  return api.get<OverviewCatalogueData>('/api/files-catalogue');
}

/**
 * The knowledge catalogue's always-resident overview. Backs the Knowledge
 * screen's OKF tab (`~/features/files/okf-panel.tsx`).
 */
export function useKnowledgeCatalogue() {
  return useQuery({
    queryKey: catalogueKeys.overview(),
    queryFn: fetchOverview,
  });
}
